import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withKeys as _withKeys, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "login-step"
}
const _hoisted_2 = { class: "logo-container" }
const _hoisted_3 = ["src"]
const _hoisted_4 = { class: "login-subtitle" }
const _hoisted_5 = {
  key: 0,
  class: "form-group"
}
const _hoisted_6 = {
  key: 1,
  class: "form-group"
}
const _hoisted_7 = {
  key: 2,
  class: "form-group"
}
const _hoisted_8 = {
  key: 3,
  class: "error-message"
}
const _hoisted_9 = {
  key: 1,
  class: "login-step"
}
const _hoisted_10 = { class: "login-subtitle" }
const _hoisted_11 = { class: "form-group" }
const _hoisted_12 = {
  key: 0,
  class: "error-message"
}
const _hoisted_13 = { class: "resend-section" }
const _hoisted_14 = {
  key: 0,
  class: "resend-text"
}
const _hoisted_15 = {
  key: 1,
  class: "help-text"
}
const _hoisted_16 = { key: 0 }

import { reactive, computed, ref, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { loginService } from "@/api/services/login";
import { useSettingsStore } from '@/api/store/settings';
import { useUserStore } from '@/api/store/user';
import { setToken, setUser } from "@/api/services/auth"
import { appService } from '@/api/services/app'


export default /*@__PURE__*/_defineComponent({
  __name: 'LoginPage',
  setup(__props) {

const settingsStore = useSettingsStore();
const userStore = useUserStore();
const router = useRouter();

// Computed property to force reactivity
const buttonColor = computed(() => settingsStore.settings.buttonColor);
const backgroundColor1 = computed(() => settingsStore.settings.backgroundColor1);
const brandingImg = computed(() => settingsStore.settings.brandingImg);

const showHelp = ref(false);
let resendInterval: any = null;

const state = reactive({
  email: "",
  phone: "",
  token: "",
  requested_token: false,
  loading: false,
  error: "",
  clinic: null,
  clinics: [],
  loginMethod: 'email' as 'email' | 'sms',
  inputMode: 'email' as 'email' | 'phone',
  resendTimer: 0
});

// Filter technical errors to show user-friendly messages
const getUserFriendlyError = (error: any, defaultMessage: string): string => {
  // Technical error patterns that should never be shown to users
  const technicalPatterns = [
    'http',
    'undefined',
    'null',
    'Cannot read properties',
    'Cannot access',
    'is not defined',
    'stream_socket',
    'socket_client',
    'Connection refused',
    'Connection could not be established',
    'ECONNREFUSED',
    'ETIMEDOUT',
    'ENOTFOUND',
    'Network Error',
    '127.0.0.1',
    'localhost',
    'at eval',
    'at Object',
    'at Array',
    'at Proxy',
    '.vue',
    '.js',
    '.ts',
    'scriptLoader',
    'node_modules',
    'webpack',
    'TypeError',
    'ReferenceError',
    'SyntaxError',
    'at line',
    'stack trace',
    'Exception',
    'Fatal error',
    'host ',
    'port ',
    'Unable to connect',
    'SSL',
    'certificate',
    'authentication failed',
    'query failed',
    'database',
    'SQL',
    'PDO',
    'mysqli'
  ];

  // Check if error message contains any technical patterns
  const isTechnicalError = (message: string): boolean => {
    if (!message || typeof message !== 'string') return true;
    const lowerMessage = message.toLowerCase();
    return technicalPatterns.some(pattern => 
      lowerMessage.includes(pattern.toLowerCase())
    );
  };

  // Try to extract a user-friendly message from the error
  if (error.response?.data?.message && typeof error.response.data.message === 'string') {
    if (!isTechnicalError(error.response.data.message)) {
      return error.response.data.message;
    }
  }

  if (error.message && typeof error.message === 'string') {
    if (!isTechnicalError(error.message)) {
      return error.message;
    }
  }

  // Default to user-friendly message
  return defaultMessage;
};

const getSettings = async () => {
  try {
    const result = await appService.fetchDisplay();
    const data = JSON.parse(result.app.settings);
    
    // Handle branding image if present
    if (result.app?.image?.file?.file_path) {
      data.brandingImg = result.app.image.file.file_path;
    }
    
    // Use store action to update settings
    settingsStore.updateSettings(data);
    
    router.replace({ path: '/' });
    
  } catch (error) {
    console.error('Error in getSettings:', error);
    // On error, just navigate normally
    router.replace({ path: '/' });
  }
}

const toggleInputMode = () => {
  state.inputMode = state.inputMode === 'email' ? 'phone' : 'email';
  state.error = "";
};

const requestToken = async (method: 'email' | 'sms') => {
  // Validate appropriate field
  if (method === 'email' && !state.email) {
    state.error = "Please enter your email address";
    return;
  }
  
  if (method === 'sms' && !state.phone) {
    state.error = "Please enter your phone number";
    return;
  }
  
  state.loading = true;
  state.error = "";
  state.loginMethod = method;

  const serviceCall = method === 'sms' ? loginService.login_sms.bind(loginService) : loginService.login.bind(loginService);

  try {
    const result = await serviceCall({
      email: method === 'email' ? state.email : state.phone,
      otp: state.token,
      requester: "clients-portal",
      clinic_id: state.clinic
    });

    if (result.logins) {
      state.clinics = [];
      result.logins.forEach((login: any) => {
        const clinicName = login.clinic?.name || login.clinic_name || `Clinic ${login.clinic_id}`;
        state.clinics.push({ value: login.clinic_id, title: clinicName });
      });
      if (state.clinics.length > 0) {
        state.clinic = state.clinics[0].value;
      }
      state.error = "";
      // Stay on step 1 to show clinic selection
    } else if (result.error) {
      // Filter error messages from API responses
      state.error = getUserFriendlyError({ message: result.error }, "Unable to send verification code. Please try again.");
    } else {
      state.requested_token = true;
      state.error = "";
      startResendTimer();
    }
  } catch (error: any) {
    console.error('Login request error:', error);
    
    // Determine user-friendly error message based on status
    let userMessage = "Unable to send verification code. Please check your email and try again.";
    
    if (error.response?.status === 404) {
      userMessage = "Email not found. Please check your email address.";
    } else if (error.response?.status === 429) {
      userMessage = "Too many attempts. Please try again later.";
    } else if (error.response?.status >= 500) {
      userMessage = "Server error. Please try again later.";
    }
    
    // Filter out any technical errors and use user-friendly message
    state.error = getUserFriendlyError(error, userMessage);
  } finally {
    state.loading = false;
  }
};

const submitToken = async () => {
  if (!state.token) {
    state.error = "Please enter your verification code";
    return;
  }

  state.loading = true;
  state.error = "";

  try {
    const result = await loginService.login_otp.bind(loginService)({
      email: state.loginMethod === 'email' ? state.email : state.phone,
      otp: state.token,
      requester: "clients-portal",
      clinic_id: state.clinic
    });

    if (result.success) {
      setToken(result.token.access_token);
      setUser(result.user);
      userStore.logged_in = true;
      userStore.user = result.user;
      await getSettings();
    } else {
      // Filter result messages as well to ensure no technical errors leak through
      const message = result.message || "Invalid code. Please try again.";
      state.error = getUserFriendlyError({ message }, "Invalid code. Please try again.");
    }
  } catch (error: any) {
    console.error('Token verification error:', error);
    
    // Determine user-friendly error message based on status
    let userMessage = "Unable to verify code. Please try again.";
    
    if (error.response?.status === 401 || error.response?.status === 403) {
      userMessage = "Invalid or expired code. Please try again.";
    } else if (error.response?.status === 429) {
      userMessage = "Too many attempts. Please try again later.";
    } else if (error.response?.status >= 500) {
      userMessage = "Server error. Please try again later.";
    }
    
    // Filter out any technical errors and use user-friendly message
    state.error = getUserFriendlyError(error, userMessage);
  } finally {
    state.loading = false;
  }
};

const goBack = () => {
  state.requested_token = false;
  state.token = "";
  state.error = "";
  clearResendTimer();
};

const resendCode = () => {
  state.token = "";
  state.error = "";
  // Ensure we resend using the same method
  requestToken(state.loginMethod);
};

const startResendTimer = () => {
  state.resendTimer = 60;
  resendInterval = setInterval(() => {
    state.resendTimer--;
    if (state.resendTimer <= 0) {
      clearResendTimer();
    }
  }, 1000);
};

const clearResendTimer = () => {
  if (resendInterval) {
    clearInterval(resendInterval);
    resendInterval = null;
  }
  state.resendTimer = 0;
};

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

onUnmounted(() => {
  clearResendTimer();
});

return (_ctx: any,_cache: any) => {
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createElementBlock("div", {
    class: "login-page-wrapper",
    style: _normalizeStyle('background-color:' + (backgroundColor1.value || '#f5f5f5'))
  }, [
    _createVNode(_component_v_container, {
      class: "login-container",
      style: _normalizeStyle('background-color:' + (backgroundColor1.value || '#f5f5f5'))
    }, {
      default: _withCtx(() => [
        (!state.requested_token)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
              _createElementVNode("div", _hoisted_2, [
                _createElementVNode("img", {
                  src: brandingImg.value,
                  class: "brand-logo",
                  alt: "Logo"
                }, null, 8, _hoisted_3)
              ]),
              _cache[12] || (_cache[12] = _createElementVNode("h1", { class: "login-title" }, "Log in", -1)),
              _createElementVNode("p", _hoisted_4, _toDisplayString(state.inputMode === 'email' ? 'Enter your email address' : 'Enter your phone number'), 1),
              (state.clinics.length > 1)
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                    _cache[8] || (_cache[8] = _createElementVNode("label", { class: "input-label" }, "Select Clinic", -1)),
                    _createVNode(_component_v_select, {
                      items: state.clinics,
                      modelValue: state.clinic,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((state.clinic) = $event)),
                      variant: "outlined",
                      density: "comfortable",
                      "hide-details": "",
                      class: "clinic-select"
                    }, null, 8, ["items", "modelValue"])
                  ]))
                : _createCommentVNode("", true),
              (state.inputMode === 'email')
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                    _cache[9] || (_cache[9] = _createElementVNode("label", { class: "input-label" }, "Your email address", -1)),
                    _createVNode(_component_v_text_field, {
                      modelValue: state.email,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((state.email) = $event)),
                      type: "email",
                      variant: "outlined",
                      density: "comfortable",
                      placeholder: "Enter your email",
                      "hide-details": "",
                      error: !!state.error,
                      class: "email-input",
                      onKeyup: _cache[2] || (_cache[2] = _withKeys(($event: any) => (requestToken('email')), ["enter"]))
                    }, null, 8, ["modelValue", "error"])
                  ]))
                : _createCommentVNode("", true),
              (state.inputMode === 'phone')
                ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                    _cache[10] || (_cache[10] = _createElementVNode("label", { class: "input-label" }, "Your phone number", -1)),
                    _createVNode(_component_v_text_field, {
                      modelValue: state.phone,
                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((state.phone) = $event)),
                      type: "tel",
                      variant: "outlined",
                      density: "comfortable",
                      placeholder: "Enter your phone number",
                      "hide-details": "",
                      error: !!state.error,
                      class: "phone-input",
                      onKeyup: _cache[4] || (_cache[4] = _withKeys(($event: any) => (requestToken('sms')), ["enter"]))
                    }, null, 8, ["modelValue", "error"])
                  ]))
                : _createCommentVNode("", true),
              (state.error && !state.requested_token)
                ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(state.error), 1))
                : _createCommentVNode("", true),
              _createVNode(_component_v_btn, {
                color: buttonColor.value || '#7c3aed',
                block: "",
                size: "large",
                class: "continue-button",
                loading: state.loading,
                disabled: (!state.email && !state.phone) || state.loading,
                onClick: _cache[5] || (_cache[5] = ($event: any) => (requestToken(state.inputMode === 'email' ? 'email' : 'sms')))
              }, {
                default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                  _createTextVNode(" Continue ", -1)
                ]))]),
                _: 1
              }, 8, ["color", "loading", "disabled"]),
              _cache[13] || (_cache[13] = _createElementVNode("div", { class: "app-links" }, [
                _createElementVNode("p", { class: "app-links-text" }, "Best experience? It's in the app"),
                _createElementVNode("div", { class: "store-badges" }, [
                  _createElementVNode("a", { href: "https://apps.apple.com/gb/app/swandoola-3-0-clients/id6746348440" }, [
                    _createElementVNode("img", {
                      src: "/img/app-store.png",
                      alt: "App Store",
                      class: "store-badge"
                    })
                  ]),
                  _createElementVNode("a", { href: "https://play.google.com/store/apps/details?id=swandoola.com&hl=en_GB&pli=1" }, [
                    _createElementVNode("img", {
                      src: "/img/android.png",
                      alt: "Google Play",
                      class: "store-badge"
                    })
                  ])
                ])
              ], -1))
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_9, [
              _createVNode(_component_v_btn, {
                icon: "",
                variant: "text",
                class: "help-button",
                onClick: _cache[6] || (_cache[6] = ($event: any) => (showHelp.value = !showHelp.value))
              }, {
                default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                  _createElementVNode("svg", {
                    width: "24",
                    height: "24",
                    viewBox: "0 0 24 24",
                    fill: "none"
                  }, [
                    _createElementVNode("circle", {
                      cx: "12",
                      cy: "12",
                      r: "10",
                      stroke: "currentColor",
                      "stroke-width": "2"
                    }),
                    _createElementVNode("path", {
                      d: "M12 16v.01M12 12c0-1.1.9-2 2-2s2-.9 2-2-.9-2-2-2-2 .9-2 2",
                      stroke: "currentColor",
                      "stroke-width": "2",
                      "stroke-linecap": "round"
                    })
                  ], -1)
                ]))]),
                _: 1
              }),
              _cache[21] || (_cache[21] = _createElementVNode("h1", { class: "login-title" }, "Enter your code", -1)),
              _createElementVNode("p", _hoisted_10, [
                _createTextVNode(" We just sent a verification code to your " + _toDisplayString(state.loginMethod === 'sms' ? 'phone' : 'email') + " ", 1),
                _createElementVNode("strong", null, _toDisplayString(state.loginMethod === 'sms' ? state.phone : state.email), 1),
                _cache[15] || (_cache[15] = _createTextVNode(". It may take a few moments to arrive ", -1))
              ]),
              _createElementVNode("div", _hoisted_11, [
                _createVNode(_component_v_text_field, {
                  modelValue: state.token,
                  "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((state.token) = $event)),
                  variant: "outlined",
                  density: "comfortable",
                  placeholder: "Enter code",
                  "hide-details": "",
                  error: !!state.error,
                  class: "token-input",
                  autofocus: "",
                  onKeyup: _withKeys(submitToken, ["enter"])
                }, null, 8, ["modelValue", "error"])
              ]),
              (state.error)
                ? (_openBlock(), _createElementBlock("div", _hoisted_12, _toDisplayString(state.error), 1))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_13, [
                (state.resendTimer > 0)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_14, " Resend in " + _toDisplayString(formatTime(state.resendTimer)), 1))
                  : (_openBlock(), _createBlock(_component_v_btn, {
                      key: 1,
                      variant: "text",
                      class: "resend-button",
                      onClick: resendCode
                    }, {
                      default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                        _createTextVNode(" Resend code ", -1)
                      ]))]),
                      _: 1
                    }))
              ]),
              (showHelp.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                    _cache[19] || (_cache[19] = _createElementVNode("p", null, [
                      _createElementVNode("strong", null, "Didn't receive the code?")
                    ], -1)),
                    _createElementVNode("ul", null, [
                      (state.loginMethod === 'email')
                        ? (_openBlock(), _createElementBlock("li", _hoisted_16, "Check your spam/junk folder"))
                        : _createCommentVNode("", true),
                      _createElementVNode("li", null, "Make sure " + _toDisplayString(state.loginMethod === 'sms' ? state.phone : state.email) + " is correct", 1),
                      _cache[17] || (_cache[17] = _createElementVNode("li", null, "Wait a few moments for the code to arrive", -1)),
                      _cache[18] || (_cache[18] = _createElementVNode("li", null, "Try resending the code", -1))
                    ])
                  ]))
                : _createCommentVNode("", true),
              _createVNode(_component_v_btn, {
                color: buttonColor.value || '#7c3aed',
                block: "",
                size: "large",
                class: "continue-button",
                loading: state.loading,
                disabled: !state.token || state.loading,
                onClick: submitToken
              }, {
                default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
                  _createTextVNode(" Verify Code ", -1)
                ]))]),
                _: 1
              }, 8, ["color", "loading", "disabled"])
            ]))
      ]),
      _: 1
    }, 8, ["style"])
  ], 4))
}
}

})